import { createContext } from 'react'

import type { SharedSchemaValues } from '../../containers/Common/SharedSchema/SharedSchema'
import type { TrueFalseOptionType } from '../../fields/RadioCard'
import type { VisibleRfiField } from '../rfi-visible-fields/rfiVisibleFields'
import type { MutationResult } from '@apollo/client'
import type {
  CountryCode,
  FieldType,
  Jurisdiction,
  L3OnboardingFieldName,
  L3OnboardingFlowGroup,
  OnboardingApplication,
  OnboardingCertification,
  OnboardingChildSection,
  OnboardingGroupDataV2,
  OnboardingRfiComment,
  OnboardingUploadDocumentWithoutLinkingInput,
  OnboardingUploadDocumentWithoutLinkingMutation,
  RfiFieldStatus,
} from '@shared/graphql'

/**
 * TODO: `SaveFieldValuesResponse` can go back to being void once backend fix in place
 * Save either returns:
 * - Array of RFI ids
 * - Undefined if save is successful but no RFIs are present
 * - False if save relational data is false.
 */
export type SaveFieldValuesResponse = string[] | void | undefined | false
export type SaveFormValuesInput =
  | SaveFieldValuesResponse
  | Array<SaveFieldValuesResponse>

export interface InstructionalMessageData {
  /**
   * Name of field.
   */
  field?: string | null
  /**
   * Name of option (for conditionals).
   */
  option?: string | null
}

export enum RfiModuleStatus {
  /**
   * The customer does not have any RFIs.
   */
  noPendingRfi = 'noPendingRfi',
  /**
   * Module is not part of rfi bundle.
   */
  notInBundle = 'notInBundle',
  /**
   * Module is part of bundle, not all fields marked as responded.
   */
  inProgress = 'inProgress',
  /**
   * All fields within rfi module have been marked as responded.
   */
  responded = 'responded',
}

export interface RfiModule {
  /**
   * Status of RFIs within current module.
   */
  status: RfiModuleStatus
  /**
   * Indicates that module is under RFI but not fully answered and cannot be saved.
   * Module is incomplete if
   * - A user comment is not provided OR
   * - Errors are present on visible rfi fields.
   */
  moduleIncomplete?: boolean
  /**
   * Creation date of rfi.
   */
  createDate?: Date
}

export interface FieldAttributes {
  /**
   * Field name.
   */
  name: string
  /**
   * Field type.
   */
  type: FieldType
  /**
   * Is field read only.
   */
  readOnly: boolean
  /**
   * Group name.
   */
  groupName: string
  /**
   * Rfi Id.
   */
  rfiId?: string | null
  /**
   * Rfi field status.
   */
  rfiStatus?: RfiFieldStatus
  /**
   * Rfi field comment.
   */
  rfiComment?: string
}

export type RfiGroupComment = Omit<OnboardingRfiComment, 'authorType'>

export interface L3OnboardingState {
  /**
   * Mapping of interaction type (hover/focus) to instructional message data.
   */
  instructionalMessage: Record<string, InstructionalMessageData | null> | null
  /**
   * Top offset used to render instructional message.
   */
  topOffset?: number
  /**
   * Current group names.
   */
  currentGroupNames?: L3OnboardingFlowGroup[]
  /**
   * Main group name.
   */
  mainGroupName?: L3OnboardingFlowGroup
  /**
   * Rfi data pertaining to the current module.
   */
  rfiModule: RfiModule
  /**
   * Group data.
   */
  groupData?: Record<
    string,
    Array<Record<L3OnboardingFieldName | 'refId', string>>
  >
  /**
   * Api Group data.
   */
  apiGroupData?: OnboardingGroupDataV2[]
  /**
   * Child sections.
   */
  childSections?: OnboardingChildSection[] | null
  /**
   * Application.
   */
  application?: OnboardingApplication | null
  /**
   * Main group value.
   */
  mainGroupValue?: Record<L3OnboardingFieldName | 'refId', string>
  /**
   * Loading.
   */
  loading: boolean
  /**
   * Should disable form?
   */
  shouldDisableForm: boolean
  /**
   * Onboarding Id.
   */
  onboardingId?: string
  /**
   * Upload document without linking mutation result.
   */
  uploadDocumentWithoutLinkingResult?: MutationResult<OnboardingUploadDocumentWithoutLinkingMutation>
  /**
   * Visible Rfi Field.
   */
  visibleRfiFields?: VisibleRfiField[]
  /**
   * Circle-generated rfi comment.
   */
  rfiCircleCommentData?: RfiGroupComment
  /**
   * User-generated rfi comment.
   */
  rfiUserCommentData?: RfiGroupComment
  /**
   * List of groups/sections under RFI.
   */
  allRfiSections?: OnboardingChildSection[]
  /**
   * True when current group is under RFI.
   */
  currentGroupUnderRfi?: boolean
  /**
   * True when customer need to revisit Certificatinos page.
   */
  needsToRecertify?: boolean
}

export interface L3OnboardingActions {
  /**
   * Show instructional message on focus.
   */
  showInstructionalMessageFocus: (e: React.FocusEvent) => void
  /**
   * Hide instructional message on blur.
   */
  hideInstructionalMessageBlur: () => void
  /**
   * Show instructional message on hover.
   */
  showInstructionalMessageHover: (e: React.MouseEvent) => void
  /**
   * Save field values.
   */
  saveFieldValues: (
    /**
     * Mapping of field names to their user filled values.
     */
    groupValuesParam:
      | Record<string, string | undefined>
      | Array<Record<string, string | undefined>>,
    /**
     * Group name.
     */
    groupNameParam?: string,
    /**
     * Additional refetch groups.
     */
    refetchGroups?: Array<L3OnboardingFlowGroup>,
    /**
     * Name of subform associated with group.
     */
    subform?: string,
  ) => Promise<SaveFieldValuesResponse>
  /**
   * Delete group value.
   */
  deleteGroupValue: (
    /**
     * RefID of the group value to be deleted.
     */
    refId: string,
    /**
     * Group name.
     */
    groupNameParam?: string,
  ) => void | Promise<void>
  /**
   * Upload document without linking.
   */
  uploadDocumentWithoutLinking: (
    document: OnboardingUploadDocumentWithoutLinkingInput,
  ) => Promise<
    OnboardingUploadDocumentWithoutLinkingMutation | null | undefined
  >
  /**
   * Submit application.
   */
  submitApplication: (
    certifications: OnboardingCertification[],
  ) => void | Promise<void>
  /**
   * Get field value from group value.
   */
  getFieldValue: (
    fieldName: string,
    groupName?: string,
    groupValueIndex?: number,
  ) => string | undefined
  /**
   * Get field value from group value, with fallback value.
   */
  getFieldValueWithFallback: (
    fieldName: string,
    groupName?: string,
    groupValueIndex?: number,
  ) => string
  /**
   * Get multi select values.
   */
  getMultiSelectValues: <ItemValue>(
    fieldName: string,
    groupName?: string,
    groupValueIndex?: number,
  ) => Array<ItemValue | undefined>
  /**
   * Get TrueFalseOption values.
   */
  getTrueFalseOptionValue: (
    fieldName: string,
    groupName?: string,
    groupValueIndex?: number,
  ) => TrueFalseOptionType
  /**
   * Get applicable jurisdiction.
   */
  getApplicableJurisdiction: (params?: {
    formPhysicalAddressCountry?: CountryCode
    formCountryOfFormation?: CountryCode
  }) => Jurisdiction | undefined
  /**
   * Get number of Beneficial Owners.
   */
  getUboCount: () => number | undefined
  /**
   * Get field attributes from group data.
   */
  getFieldInGroupData: (
    fieldName: string,
    refId?: string,
  ) => FieldAttributes | undefined
  /**
   * Update visible RFI fields.
   */
  updateVisibleRfiFields: (fields: VisibleRfiField[] | undefined) => void
  /**
   * On form submit.
   */
  onFormSubmit: (
    submitFn: () => Promise<SaveFormValuesInput>,
    sharedValues: SharedSchemaValues,
  ) => Promise<void>
}

export const DEFAULT_VALUES: L3OnboardingState = {
  instructionalMessage: null,
  loading: true,
  shouldDisableForm: false,
  rfiModule: {
    status: RfiModuleStatus.noPendingRfi,
  },
  visibleRfiFields: [],
}

export const DEFAULT_ACTIONS: L3OnboardingActions = {
  showInstructionalMessageHover: () => null,
  showInstructionalMessageFocus: () => null,
  hideInstructionalMessageBlur: () => null,
  saveFieldValues: () => Promise.resolve(undefined),
  deleteGroupValue: () => undefined,
  uploadDocumentWithoutLinking: () => Promise.resolve(undefined),
  submitApplication: () => undefined,
  getFieldValue: () => undefined,
  getFieldValueWithFallback: () => '',
  getMultiSelectValues: () => [],
  getTrueFalseOptionValue: () => '',
  getApplicableJurisdiction: () => undefined,
  getUboCount: () => undefined,
  getFieldInGroupData: () => undefined,
  updateVisibleRfiFields: () => undefined,
  onFormSubmit: () => Promise.resolve(undefined),
}

export const L3OnboardingContext = createContext<
  [L3OnboardingState, L3OnboardingActions]
>([DEFAULT_VALUES, DEFAULT_ACTIONS])
